/**
 * The Daily Dig (docs/spec/07 §2): today's generated cave, your best and attempts, PLAY. After a
 * run: the result and a share line to copy; if it was won and the online board answers, a prompt
 * to sign it, then the top ten. The board is optional: without a server none of it shows.
 */

import { GAME_NAME } from '../../app/brand';
import { dailyCave, dailyNumber } from '../../caves/generator';
import type { CaveDef } from '../../caves/types';
import type { BoardEntry } from '../../platform/leaderboard';
import { fetchDailyBoard, submitDaily } from '../../platform/leaderboard';
import type { DailyRecord } from '../../platform/records';
import type { Screen } from '../../render/screen';
import type { DailyRun, KeyInput, MenuAction, PointerInput, SceneHost } from '../scene';
import { UiScene } from '../kit/base';
import { drawButton } from '../kit/chrome';
import type { EditorLook } from '../kit/initialsEditor';
import { drawEditor, editorPointer, rememberedInitials, rememberInitials } from '../kit/initialsEditor';
import type { InitialsStep } from '../kit/initialsModel';
import { initialsAction, initialsKey, startInitials } from '../kit/initialsModel';
import type { Rect } from '../kit/menuModel';
import { inRect, wrapIndex } from '../kit/menuModel';
import { ACCENT } from '../kit/theme';
import { dayTitle, runToShare, shareLine } from './dailyModel';
import { drawBoard, drawCave, drawResult, drawShare, drawSignPanel, drawToday } from './dailyView';

type ButtonId = 'play' | 'copy' | 'sign';
type Sign = 'none' | 'editing' | 'sending';

const BUTTON_W = 92;
const BUTTON_Y = 174;
const SIGN_EDITOR: EditorLook = { cx: 160, y: 84, scale: 2 };

export class DailyScene extends UiScene {
  protected readonly heading = 'DAILY DIG';
  private readonly date: { year: number; month: number; day: number };
  private readonly day: number;
  private readonly def: CaveDef;
  private record: DailyRecord;
  private board: BoardEntry[] | null = null;
  private mine: BoardEntry | null = null;
  private sign: Sign = 'none';
  private offered = false;
  private initials = startInitials(rememberedInitials());
  private sel = 0;
  private note = '';
  private noteAt = -9999;
  private alive = false;
  private asked = 0;

  constructor(host: SceneHost, private readonly last: DailyRun | undefined) {
    super(host);
    this.date = host.services.today();
    this.day = dailyNumber(this.date.year, this.date.month, this.date.day);
    this.def = dailyCave(this.date.year, this.date.month, this.date.day);
    this.record = host.services.records.daily(this.day);
  }

  override enter(): void {
    super.enter();
    this.alive = true;
    this.record = this.services.records.daily(this.day);
    this.fetchBoard();
  }

  leave(): void {
    this.alive = false;
  }

  private fetchBoard(): void {
    const ask = ++this.asked;
    void fetchDailyBoard(this.day).then((board) => {
      if (!this.alive || ask !== this.asked || !board) return;
      this.board = board;
      if (this.last?.won === true && this.last.proof !== undefined && !this.offered) {
        this.offered = true;
        this.sign = 'editing';
        this.sound('select');
        this.say('Sign the online board: enter your initials.');
      }
    });
  }

  private get share(): string | null {
    const run = runToShare(this.last, this.record);
    return run ? shareLine(GAME_NAME, this.day, run) : null;
  }

  private buttons(): { id: ButtonId; label: string }[] {
    const play = this.record.attempts > 0 ? 'PLAY AGAIN' : 'PLAY';
    const out: { id: ButtonId; label: string }[] = [{ id: 'play', label: play }];
    if (this.share) out.push({ id: 'copy', label: 'COPY' });
    const canSign = this.board !== null && this.last?.won === true && this.last.proof !== undefined && this.sign === 'none' && !this.mine;
    if (canSign) out.push({ id: 'sign', label: 'SIGN BOARD' });
    return out;
  }

  private buttonRect(i: number, n: number): Rect {
    const total = n * BUTTON_W + (n - 1) * 8;
    return { x: Math.floor(160 - total / 2) + i * (BUTTON_W + 8), y: BUTTON_Y, w: BUTTON_W, h: 15 };
  }

  protected override describe(): string {
    const r = this.record;
    const best = r.won ? `Your best today ${r.best}.` : 'Not finished yet today.';
    return `Daily Dig number ${this.day}: ${this.def.name}. ${best} ${r.attempts} attempts.`;
  }

  protected override hint(): string {
    return this.sign === 'none' ? `◄► CHOOSE   ${this.words().ok} OK   ${this.words().back} BACK` : '';
  }

  protected paint(screen: Screen): void {
    const { year, month, day } = this.date;
    screen.text(dayTitle(this.day, year, month, day), 160, 20, ACCENT, 'center');
    drawCave(screen, this.def, this.ms);
    if (this.board) drawBoard(screen, this.board, this.mine, this.ms);
    else drawToday(screen, this.record, this.def.hint);
    if (this.last) drawResult(screen, this.last);
    const line = this.share;
    if (line) drawShare(screen, line, this.note, this.ms - this.noteAt);
    const buttons = this.buttons();
    this.sel = Math.min(this.sel, buttons.length - 1);
    buttons.forEach((b, i) => {
      const state = this.sign === 'none' && i === this.sel ? 'selected' : 'idle';
      drawButton(screen, this.buttonRect(i, buttons.length), b.label, state, this.ms);
    });
    if (this.sign === 'editing' || this.sign === 'sending') {
      drawSignPanel(screen, this.sign === 'sending' ? 'SENDING...' : '', this.ms);
      if (this.sign === 'editing') drawEditor(screen, this.initials, SIGN_EDITOR, this.ms);
    }
  }

  private flash(text: string): void {
    this.note = text;
    this.noteAt = this.ms;
    this.say(text);
  }

  private press(id: ButtonId | undefined): void {
    if (id === 'play') {
      this.sound('select');
      this.host.go({ to: 'play', request: { mode: 'daily', ...this.date } });
    } else if (id === 'copy') this.copy();
    else if (id === 'sign') {
      this.sign = 'editing';
      this.sound('select');
    }
  }

  private copy(): void {
    const line = this.share;
    if (!line) return this.sound('error');
    this.sound('select');
    const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clip) return this.flash('COPY FAILED');
    clip.writeText(line).then(
      () => this.alive && this.flash('COPIED!'),
      () => this.alive && this.flash('COPY FAILED'),
    );
  }

  private editStep(step: InitialsStep): void {
    this.initials = step.state;
    if (step.sound) this.sound(step.sound);
    if (step.done) this.send();
  }

  private send(): void {
    const run = this.last;
    if (!run?.proof) return;
    this.sign = 'sending';
    rememberInitials(this.initials.letters);
    const entry: BoardEntry = { initials: this.initials.letters, score: run.score, seconds: run.seconds };
    void submitDaily(this.day, entry, run.proof).then((ok) => {
      if (!this.alive) return;
      this.sign = 'none';
      if (ok) {
        this.mine = entry;
        this.flash('SIGNED!');
        this.fetchBoard();
      } else this.flash('COULD NOT SEND');
    });
  }

  keydown(k: KeyInput): boolean {
    if (this.sign !== 'editing') return this.sign === 'sending';
    const step = initialsKey(this.initials, k.code, k.key);
    if (!step) return false;
    this.editStep(step);
    return true;
  }

  protected override back(): void {
    if (this.sign === 'editing') {
      this.sign = 'none';
      this.sound('back');
    } else if (this.sign === 'none') super.back();
  }

  protected onAction(a: MenuAction): void {
    if (this.sign === 'editing') return this.editStep(initialsAction(this.initials, a));
    if (this.sign !== 'none') return;
    const n = this.buttons().length;
    if (a === 'left' || a === 'up') this.sel = wrapIndex(this.sel - 1, n);
    else if (a === 'right' || a === 'down') this.sel = wrapIndex(this.sel + 1, n);
    else if (a === 'confirm') return this.press(this.buttons()[this.sel]?.id);
    else return;
    this.sound('move');
    this.say(this.buttons()[this.sel]?.label ?? '');
  }

  protected override onPointer(p: PointerInput): void {
    if (this.sign === 'editing') {
      const step = p.button === 0 ? editorPointer(this.initials, SIGN_EDITOR, p.kind, p.x, p.y, p.delta) : null;
      if (step) this.editStep(step);
      return;
    }
    if (this.sign !== 'none') return;
    const buttons = this.buttons();
    const i = buttons.findIndex((_, k) => inRect(this.buttonRect(k, buttons.length), p.x, p.y));
    if (i < 0) return;
    if (p.kind === 'move' && i !== this.sel) {
      this.sel = i;
      this.sound('move');
    }
    if (p.kind === 'down' && p.button === 0) this.press(buttons[i]?.id);
  }
}
